import React, { useEffect, useMemo } from 'react';
import styled, { css } from 'styled-components';
import { Formik } from 'formik';
import { FormItem } from 'src/common/components/FormItem';
import { TextControl } from 'src/common/components/TextControl';
import { object, string } from 'yup';
import { Button } from 'src/common/components/Button';
import { ForgotPasswordSuccessRoute, LoginRoute } from 'src/public/publicRoutes';
import { media } from 'src/config/breakpoints';
import { AuthLayout } from 'src/public/components/auth/AuthLayout';
import { AuthBox } from 'src/public/components/auth/AuthBox';
import { AuthTitle } from 'src/public/components/auth/AuthTitle';
import { AuthForm } from 'src/public/components/auth/AuthForm';
import { AuthFields } from 'src/public/components/auth/AuthFields';
import { AuthButton } from 'src/public/components/auth/AuthButton';
import { AuthLogInLinkContainer } from 'src/public/components/auth/AuthLogInLinkContainer';
import { AuthPanelLeft } from 'src/public/components/auth/AuthPanelLeft';
import { AuthPanelRight } from 'src/public/components/auth/AuthPanelRight';
import { AuthText } from 'src/public/components/auth/AuthText';
import { mapToLoginDTO, RequestResetPasswordFormModel } from 'src/common/models/auth';
import { useLoggedOutGuard } from 'src/common/guards/useLoggedOutGuard';
import { pipe } from 'fp-ts/function';
import { snackbarActions } from 'src/state/notification/notificationActions';
import { useDispatch, useSelector } from 'react-redux';
import { useMutation } from 'react-query';
import { requestResetPassword } from 'src/common/services/user';
import { GatsbyLink } from 'src/common/migration/GatsbyLink';
import { WithClientOnly } from 'src/lib/SSR/ClientOnly/ClientOnly';
import { VendorGetStaticProps } from 'vendor/utils/VendorGetStaticProps';
import { pageContextActions } from 'src/state/pageContext/pageContextActions';
import { PageContextType } from 'src/state/pageContext/pageContextReducer';
import { requestResetPasswordFormCacheSelector } from 'src/state/pageContext/pageContextSelectors';
import { useRouter } from 'next/router';

const StyledAuthPanelLeft = styled(AuthPanelLeft)`
  ${media.w.min.px992(css`
    padding-bottom: 8.2vw;
    padding-top: 8.2vw;
  `)};

  ${media.w.min.px1440(css`
    padding-bottom: 7.3125rem;
    padding-top: 7.3125rem;
  `)};
`;

const StyledAuthPanelRight = styled(AuthPanelRight)`
  ${media.w.min.px992(css`
    padding-top: 8.2vw;
  `)};

  ${media.w.min.px1440(css`
    padding-top: 7.3125rem;
  `)};
`;

const defaultInitialValues: RequestResetPasswordFormModel = {
  email: '',
};

const validationSchema = object().shape({
  email: string().required('Pole obowiązkowe').email('Podaj poprawny adres email'),
});

const ForgotPasswordPage = WithClientOnly(() => {
  const shouldRender = useLoggedOutGuard();
  const mutation = useMutation(requestResetPassword);
  const dispatch = useDispatch();
  const requestPasswordResetEmailCache = useSelector(requestResetPasswordFormCacheSelector);
  const router = useRouter();

  const initialValues = useMemo(() => requestPasswordResetEmailCache || defaultInitialValues, []);

  useEffect(() => {
    dispatch(pageContextActions.cleanPageContext(PageContextType.RequestResetPasswordFormCache));
  }, []);

  if (!shouldRender) {
    return null;
  }

  return (
    <AuthLayout title="Odzyskiwanie hasła">
      <AuthBox>
        <StyledAuthPanelLeft>
          <AuthTitle>Odzyskaj hasło</AuthTitle>

          <AuthText>
            Nie pamiętasz hasła do swojego konta?
            <br />
            Wpisz swój adres e-mail, jeżeli mamy go w&nbsp;systemie, wyślemy link do zmiany hasła.
          </AuthText>
        </StyledAuthPanelLeft>

        <StyledAuthPanelRight>
          <Formik<RequestResetPasswordFormModel>
            initialValues={initialValues}
            validationSchema={validationSchema}
            onSubmit={(values, { resetForm, setSubmitting }) =>
              pipe(values, mapToLoginDTO, mutation.mutateAsync, (x) =>
                x
                  .then(() => {
                    dispatch(pageContextActions.setRequestResetPasswordFormCache(values));
                    dispatch(
                      snackbarActions.enqueueSnackbar({
                        message: 'Email został wysłany',
                        options: { variant: 'success' },
                      }),
                    );
                    resetForm({ values: { email: '' } });
                    router.push(ForgotPasswordSuccessRoute);
                  })
                  .finally(() => {
                    setSubmitting(false);
                  }),
              )
            }
          >
            {({ isSubmitting }) => (
              <AuthForm noValidate>
                <AuthFields>
                  <FormItem name="email">
                    <TextControl
                      type="email"
                      name="email"
                      label="Adres e-mail"
                      disabled={isSubmitting}
                    />
                  </FormItem>
                </AuthFields>

                <AuthButton type="submit" loading={mutation.isLoading}>
                  Wyślij
                </AuthButton>

                <AuthLogInLinkContainer>
                  <Button decoration="underline" kind="link" as={GatsbyLink} to={LoginRoute}>
                    Powrót do logowania
                  </Button>
                </AuthLogInLinkContainer>
              </AuthForm>
            )}
          </Formik>
        </StyledAuthPanelRight>
      </AuthBox>
    </AuthLayout>
  );
});

export const getStaticProps = VendorGetStaticProps(() => ({
  props: {},
  revalidate: false,
}));

export default ForgotPasswordPage;
